import { useEffect, useState } from "react";
import { Plus, Power, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function Cities() {
  const [cities, setCities] = useState([]);
  const [search, setSearch] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", state: "", tag: "", image: "" });
  const emptyForm = { name: "", state: "", tag: "", image: "" };

  const load = () => api.get("/cities").then(({ data }) => setCities(data));
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (c) => { setEditId(c.id); setForm({ name: c.name || "", state: c.state || "", tag: c.tag || "", image: c.image || "" }); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("City name required"); return; }
    try {
      if (editId) {
        const current = cities.find((c) => c.id === editId);
        await api.put(`/admin/cities/${editId}`, { ...form, active: current?.active ?? true });
        toast.success("City updated");
      } else {
        await api.post("/admin/cities", form);
        toast.success("City added");
      }
      setOpen(false); setForm(emptyForm); setEditId(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (id) => { await api.patch(`/admin/cities/${id}/toggle`); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this city? Its localities will also be removed.")) return; try { await api.delete(`/admin/cities/${id}`); toast.success("City deleted"); load(); } catch (e) { toast.error(apiError(e)); } };
  const bulkSave = async () => {
    const cities = bulkText.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const parts = l.split(","); return { name: (parts[0] || "").trim(), state: (parts[1] || "").trim() }; }).filter((c) => c.name);
    if (!cities.length) { toast.error("Enter at least one city"); return; }
    try { const { data } = await api.post("/admin/cities/bulk", { cities }); toast.success(`Added ${data.added}, skipped ${data.skipped} existing`); setBulkOpen(false); setBulkText(""); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = cities.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-extrabold">Cities</h1><p className="text-muted-foreground text-sm font-medium">Control which cities buyers can select.</p></div>
        <div className="flex gap-2">
          <Button data-testid="bulk-city-btn" variant="outline" onClick={() => setBulkOpen(true)} className="font-bold gap-1"><Upload className="h-4 w-4" /> Bulk Upload</Button>
          <Button data-testid="add-city-btn" onClick={openAdd} className="font-bold gap-1"><Plus className="h-4 w-4" /> Add City</Button>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editId ? "Edit City" : "Add City"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>City Name</Label><Input data-testid="city-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>State</Label><Input data-testid="city-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
              <div><Label>Tag</Label><Input data-testid="city-tag" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} placeholder="e.g. Tile Hub" /></div>
              <div><Label>Image URL</Label><Input data-testid="city-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://…" /></div>
              <Button data-testid="save-city" onClick={save} className="w-full font-bold">{editId ? "Update City" : "Save City"}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Input data-testid="city-search" placeholder="Search city…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bulk Upload Cities</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">One city per line as <span className="font-mono">City, State</span>. New cities appear automatically as big stamps on the buyer's city screen.</p>
            <textarea data-testid="bulk-city-text" value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={9}
              className="w-full rounded-md border p-3 text-sm font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              placeholder={"Coimbatore, Tamil Nadu\nKochi, Kerala\nSalem, Tamil Nadu\nMadurai, Tamil Nadu"} />
            <Button data-testid="save-bulk-city" onClick={bulkSave} className="w-full font-bold">Upload Cities</Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`city-${c.id}`} className="bg-card border rounded-xl overflow-hidden">
            <div className="relative aspect-video bg-secondary">
              {c.image && <img src={c.image} alt={c.name} className="h-full w-full object-cover" />}
              <Badge className={`absolute top-2 right-2 border ${c.active ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-slate-200 text-slate-600"}`}>{c.active ? "Active" : "Inactive"}</Badge>
            </div>
            <div className="p-3 flex items-center justify-between">
              <div><div className="font-heading font-bold">{c.name}</div><div className="text-xs text-muted-foreground">{c.state} · {c.tag}</div></div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" data-testid={`edit-city-${c.id}`} onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" data-testid={`toggle-city-${c.id}`} onClick={() => toggle(c.id)}><Power className={`h-4 w-4 ${c.active ? "text-rose-600" : "text-emerald-600"}`} /></Button>
                <Button size="sm" variant="outline" data-testid={`delete-city-${c.id}`} onClick={() => remove(c.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
